// /src/pages/AdminModuleManagement.jsx

import React, { useState, useEffect } from 'react';
import {
    getAllModulesForAdmin,
    importModulesCsv,
    uploadModuleEnrollmentForYear,
    saveModuleEnrollmentForYear,
    getModuleEnrollments,
    updateModuleDetails,
    getModuleLeaders,
    getSystemSettings,
} from '../../api/adminApi';
import { getViewSurveyQuestions } from '../../api/moduleApi'; // Reused from moduleApi
import SurveyPreviewModal from '../../components/ui/SurveyPreview.jsx';
import RosterEditorModal from '../../components/ui/RosterEditorModal.jsx';

// Leaders will be fetched from the backend
const SEMESTER_OPTIONS = ['1', '2'];

const getCurrentAcademicYearStart = (startMonth, startDay, referenceDate = new Date()) => {
    const currentYear = referenceDate.getFullYear();
    const currentMonth = referenceDate.getMonth() + 1;
    const currentDay = referenceDate.getDate();

    if (currentMonth < startMonth || (currentMonth === startMonth && currentDay < startDay)) {
        return currentYear - 1;
    }

    return currentYear;
};

// --- Module Data Structure Note ---
// Module objects fetched by getAllModulesForAdmin include:
// { module_id, module_code, module_name, module_leader_id, semester, active_survey_id, ... }
// active_survey_id will be present if there's an Active survey for the module, otherwise null.

const AdminModuleManagement = () => {
    const [allModules, setAllModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for CSV Imports
    const [moduleFile, setModuleFile] = useState(null);
    const [enrollmentFile, setEnrollmentFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [academicYearStartDate, setAcademicYearStartDate] = useState({ month: 9, day: 22 });
    const [enrollmentYear, setEnrollmentYear] = useState(() => getCurrentAcademicYearStart(9, 22));
    const [hasTouchedEnrollmentYear, setHasTouchedEnrollmentYear] = useState(false);

    // State for Editing
    const [selectedModule, setSelectedModule] = useState(null);
    const [editingModule, setEditingModule] = useState(null); // Local state for changes
    const [isUpdating, setIsUpdating] = useState(false);
    const [leaders, setLeaders] = useState([]);
    const [moduleEnrollments, setModuleEnrollments] = useState([]);
    const [showReplaceUploader, setShowReplaceUploader] = useState(false);
    const [isRosterEditorOpen, setIsRosterEditorOpen] = useState(false);
    const [isSavingRoster, setIsSavingRoster] = useState(false);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await getSystemSettings();
                if (settings && settings.academic_year_start_date) {
                    const [m, d] = settings.academic_year_start_date.split('-').map(Number);
                    if (Number.isInteger(m) && Number.isInteger(d)) {
                        setAcademicYearStartDate({ month: m, day: d });
                        if (!hasTouchedEnrollmentYear) {
                            setEnrollmentYear(getCurrentAcademicYearStart(m, d));
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to load system settings:', err);
            }
        };

        loadSettings();
    }, [hasTouchedEnrollmentYear]);
    
    // Refetch enrollments when selected module or enrollmentYear changes
    useEffect(() => {
        if (!selectedModule) return;
        (async () => {
            try {
                const res = await getModuleEnrollments(selectedModule.module_id, enrollmentYear);
                setModuleEnrollments(res.rows || []);
            } catch (err) {
                console.error('Failed to load enrollments for year change:', err);
                setModuleEnrollments([]);
            }
        })();
    }, [selectedModule, enrollmentYear]);

    const refreshModuleEnrollments = async (moduleId = selectedModule?.module_id, year = enrollmentYear) => {
        if (!moduleId) return;
        try {
            const res = await getModuleEnrollments(moduleId, year);
            setModuleEnrollments(res.rows || []);
        } catch (err) {
            console.error('Failed to load enrollments:', err);
            setModuleEnrollments([]);
        }
    };

    // --- State for Survey Preview ---
    const [isViewing, setIsViewing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [isFetchingPreview, setIsFetchingPreview] = useState(false);

    // --- Fetch Modules ---
    const fetchModules = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllModulesForAdmin();
            setAllModules(data);
            // Fetch leaders list for the Module Leader dropdown
            try {
                const leaderList = await getModuleLeaders();
                setLeaders(leaderList || []);
            } catch (lerr) {
                console.error('Failed to fetch leaders:', lerr);
            }
            if (data.length > 0) {
                // Automatically select the first module for editing if data exists
                setSelectedModule(data[0]);
                setEditingModule(data[0]);
            }
        } catch (err) {
            setError(err.message || "Failed to load modules.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModules();
    }, []);

    // --- HANDLERS for CSV Imports ---

    const handleModuleImport = async () => {
        if (!moduleFile) {
            alert("Please select a module list CSV file first.");
            return;
        }
        setIsImporting(true);
        try {
            const result = await importModulesCsv(moduleFile);
            alert(result.message);
            // Re-fetch the module list after successful import
            await fetchModules();
            setModuleFile(null); // Clear file input
        } catch (err) {
            alert(`Module Import Failed: ${err.message}`);
        } finally {
            setIsImporting(false);
        }
    };

    const handleEnrollmentUpload = async () => {
        if (!selectedModule || !enrollmentFile) {
            alert("Please select a module and an enrollment CSV file.");
            return;
        }
        setIsImporting(true);
        try {
            const result = await uploadModuleEnrollmentForYear(selectedModule.module_id, enrollmentFile, enrollmentYear);
            // Backend returns { success: true, students_processed: <n> }
            if (result && result.success) {
                const count = result.students_processed != null ? result.students_processed : result.studentsProcessed || 0;
                alert(`Enrollment upload successful. Students processed: ${count}`);
            } else if (result && result.message) {
                alert(result.message);
            } else {
                alert('Enrollment upload completed.');
            }
            setEnrollmentFile(null); // Clear file input
            // refresh enrollments for this module after upload
            try {
                await refreshModuleEnrollments(selectedModule.module_id, enrollmentYear);
                setShowReplaceUploader(false);
            } catch (err) {
                console.error('Failed to refresh enrollments after upload:', err);
            }
        } catch (err) {
            alert(`Enrollment Upload Failed: ${err.message}`);
        } finally {
            setIsImporting(false);
        }
    };

    // --- HANDLERS for Module Editing ---

    const handleModuleSelect = (e) => {
        const moduleId = Number(e.target.value);
        const module = allModules.find(m => m.module_id === moduleId);
        setSelectedModule(module);
        setEditingModule(module);
        // fetch enrollments for display
        refreshModuleEnrollments(moduleId, enrollmentYear);
        setShowReplaceUploader(false);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditingModule(prev => ({ ...prev, [name]: name === 'module_leader_id' ? (value ? Number(value) : null) : value }));
    };

    const handleModuleUpdate = async () => {
        if (!editingModule) return;
        setIsUpdating(true);

        const payload = {
            module_leader_id: editingModule.module_leader_id,
            semester: editingModule.semester
        };

        try {
            await updateModuleDetails(editingModule.module_id, payload);
            alert(`Module ${editingModule.module_code} updated successfully!`);
            // Re-fetch all modules to update the main list and dropdown
            await fetchModules();
        } catch (err) {
            alert(`Update Failed: ${err.message}`);
        } finally {
            setIsUpdating(false);
        }
    };

    // --- HANDLERS for Viewing Survey ---

    const handleViewSurvey = async () => {
        if (!selectedModule) return;

        // The backend now provides active_survey_id for modules with Active status surveys
        const surveyIdentifier = selectedModule.active_survey_id; 

        if (!surveyIdentifier) {
            alert("This module does not currently have an active survey. Deploy a survey from the Pending Deployments or Deployed Surveys page first.");
            return;
        }

        setIsFetchingPreview(true);
        setPreviewData(null);
        
        try {
            // Pass the survey_id to retrieve the deployed survey questions
            const data = await getViewSurveyQuestions({ surveyId: surveyIdentifier }); 
            setPreviewData(data);
            setIsViewing(true); // Open the modal
        } catch (error) {
            alert(`Failed to load survey preview: ${error.message}. Check browser console for network details.`);
            console.error("View Preview Error:", error);
        } finally {
            setIsFetchingPreview(false);
        }
    };

    const handleSaveRosterEdits = async (students) => {
        if (!selectedModule) return;
        setIsSavingRoster(true);
        try {
            const res = await saveModuleEnrollmentForYear(selectedModule.module_id, enrollmentYear, students);
            alert(`Roster saved. Students processed: ${res.students_processed}`);
            await refreshModuleEnrollments(selectedModule.module_id, enrollmentYear);
            setIsRosterEditorOpen(false);
        } catch (err) {
            alert(`Failed to save roster: ${err.message}`);
        } finally {
            setIsSavingRoster(false);
        }
    };


    // --- JSX Rendering ---

    if (loading) {
        return <div className="p-6 text-center text-indigo-500">Loading module data...</div>;
    }

    return (
        <div className="p-6 bg-white rounded-xl shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-indigo-700">Module & Enrollment Management</h2>
            <p className="text-gray-600 mb-8">
                Use this page to initially import your module list and student enrollments. You can also update module details here.
            </p>

            {/* ----------------------------------- */}
            {/* 1. Module CSV Import (Always visible) */}
            {/* ----------------------------------- */}
            <div className="p-6 border border-indigo-200 rounded-xl bg-indigo-50 mb-8">
                <h3 className="text-xl font-semibold text-indigo-700 mb-3">Module List Import</h3>
                <p className="text-sm text-gray-700 mb-4">Upload a CSV to create/update all modules and assign leaders. Expected columns: <code>ModuleCode</code>, <code>ModuleName</code>, <code>ModuleLeaderInitials</code>, <code>Semester</code>.</p>
                <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setModuleFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-100 hover:file:bg-indigo-200 mb-4"
                />
                <button
                    onClick={handleModuleImport}
                    className="w-full bg-green-600 text-white font-semibold py-2 rounded-lg hover:bg-green-700 transition disabled:bg-green-400"
                    disabled={isImporting || !moduleFile}
                >
                    {isImporting ? 'Processing Import...' : 'Import/Update Modules'}
                </button>
                {error && <p className="mt-4 text-sm text-red-600">Error: {error}</p>}
            </div>

            {/* ----------------------------------- */}
            {/* 2. Module Editing & Enrollment Upload (Conditional) */}
            {/* ----------------------------------- */}
            {allModules.length > 0 && (
                <>
                    <h3 className="text-2xl font-bold mb-4 text-indigo-700">Existing Modules</h3>

                    {/* Module Select */}
                    <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                        <label htmlFor="module-select" className="block text-sm font-medium text-gray-700 mb-2">
                            Select Module to Edit/Enroll
                        </label>
                        <select
                            id="module-select"
                            value={selectedModule?.module_id || ''}
                            onChange={handleModuleSelect}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            {allModules.map(module => (
                                <option key={module.module_id} value={module.module_id}>
                                    {module.module_code} - {module.module_name} {(!module.module_leader_id) && <span> [No leader]</span>}
                                </option>
                            ))}
                        </select>
                    </div>

                    {editingModule && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* Module Details Editor */}
                            <div className="lg:col-span-2 p-6 border border-gray-200 rounded-xl shadow-md space-y-4">
                                <h4 className="text-xl font-semibold text-gray-800">Editing: {editingModule.module_code}</h4>

                                {/* Module Leader */}
                                <div>
                                    <label htmlFor="module_leader_id" className="block text-sm font-medium text-gray-700 mb-1">Module Leader</label>
                                    <select
                                        id="module_leader_id"
                                        name="module_leader_id"
                                        value={editingModule.module_leader_id || ''}
                                        onChange={handleEditChange}
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="" disabled>Select a Leader</option>
                                        {leaders.map(leader => (
                                            <option key={leader.account_id} value={leader.account_id}>
                                                {leader.display_name || `${leader.first_name || ''} ${leader.last_name || ''} (${leader.email || ''})`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Semester */}
                                <div>
                                    <label htmlFor="semester" className="block text-sm font-medium text-gray-700 mb-1">Semester</label>
                                    <select
                                        id="semester"
                                        name="semester"
                                        value={editingModule.semester || ''}
                                        onChange={handleEditChange}
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="" disabled>Select a Semester</option>
                                        {SEMESTER_OPTIONS.map(sem => (
                                            <option key={sem} value={sem}>{sem}</option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    onClick={handleModuleUpdate}
                                    className="w-full bg-indigo-600 text-white font-semibold py-2 rounded-lg hover:bg-indigo-700 transition disabled:bg-indigo-400"
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? 'Updating...' : 'Save Module Changes'}
                                </button>

                                {/* View Survey Button */}
                                <button
                                    onClick={handleViewSurvey}
                                    disabled={isFetchingPreview}
                                    className="w-full bg-blue-500 text-white font-semibold py-2 rounded-lg hover:bg-blue-600 transition mt-2 disabled:bg-blue-400"
                                >
                                    {isFetchingPreview ? 'Loading Survey...' : 'View Current Survey'}
                                </button>

                            </div>

                            {/* Enrollment CSV Upload / Roster Preview */}
                            <div className="p-6 border border-green-200 rounded-xl bg-green-50 shadow-md h-fit">
                                <h4 className="text-xl font-semibold text-green-700 mb-3">Enrollment (Roster)</h4>
                                <p className="text-sm text-gray-700 mb-4">Expected columns: <code>StudentID</code>, <code>Email</code>.</p>
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year (start)</label>
                                    <input
                                        type="number"
                                        value={enrollmentYear}
                                        onChange={(e) => {
                                            setHasTouchedEnrollmentYear(true);
                                            setEnrollmentYear(Number(e.target.value));
                                        }}
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                {moduleEnrollments && moduleEnrollments.length > 0 ? (
                                    <div>
                                        <div className="text-sm text-gray-700 mb-2">Current roster: <span className="font-semibold">{moduleEnrollments.length} students</span></div>
                                        <ul className="text-xs text-gray-600 mb-3 list-disc list-inside max-h-40 overflow-auto">
                                            {moduleEnrollments.slice(0,20).map((r, idx) => (
                                                <li key={idx}>{r.student_email}{r.student_identifier ? ` — ${r.student_identifier}` : ''}</li>
                                            ))}
                                        </ul>
                                        <button
                                            onClick={() => setIsRosterEditorOpen(true)}
                                            className="w-full mb-2 bg-indigo-600 text-white font-semibold py-2 rounded-lg hover:bg-indigo-700 transition"
                                            disabled={isImporting}
                                        >
                                            Edit Roster Manually
                                        </button>
                                        <button onClick={() => setShowReplaceUploader(prev => !prev)} className="w-full mb-3 bg-yellow-500 text-white py-2 rounded-lg hover:bg-yellow-600">{showReplaceUploader ? 'Cancel Replace' : 'Replace Roster (upload CSV)'}</button>
                                        {showReplaceUploader ? (
                                            <>
                                                <input
                                                    type="file"
                                                    accept=".csv"
                                                    onChange={(e) => setEnrollmentFile(e.target.files[0])}
                                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-100 hover:file:bg-green-200 mb-4"
                                                />
                                                <button
                                                    onClick={handleEnrollmentUpload}
                                                    className="w-full bg-green-600 text-white font-semibold py-2 rounded-lg hover:bg-green-700 transition disabled:bg-green-400"
                                                    disabled={isImporting || !enrollmentFile}
                                                >
                                                    {isImporting ? 'Uploading Register...' : 'Upload Enrollment'}
                                                </button>
                                            </>
                                        ) : null}
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={(e) => setEnrollmentFile(e.target.files[0])}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-100 hover:file:bg-green-200 mb-4"
                                        />
                                        <button
                                            onClick={handleEnrollmentUpload}
                                            className="w-full bg-green-600 text-white font-semibold py-2 rounded-lg hover:bg-green-700 transition disabled:bg-green-400"
                                            disabled={isImporting || !enrollmentFile}
                                        >
                                            {isImporting ? 'Uploading Register...' : 'Upload Enrollment'}
                                        </button>
                                        <button
                                            onClick={() => setIsRosterEditorOpen(true)}
                                            className="w-full mt-2 bg-indigo-600 text-white font-semibold py-2 rounded-lg hover:bg-indigo-700 transition"
                                            disabled={isImporting}
                                        >
                                            Edit Roster Manually
                                        </button>
                                    </>
                                )}
                            </div>

                        </div>
                    )}
                </>
            )}

            {/* --- Survey Preview Modal (NEW) --- */}
            {isViewing && previewData && (
                <SurveyPreviewModal
                    requestData={previewData}
                    onClose={() => setIsViewing(false)}
                />
            )}
            <RosterEditorModal
                isOpen={isRosterEditorOpen}
                onClose={() => setIsRosterEditorOpen(false)}
                moduleName={selectedModule ? `${selectedModule.module_code} - ${selectedModule.module_name}` : 'Module'}
                year={enrollmentYear}
                initialRows={moduleEnrollments}
                onSave={handleSaveRosterEdits}
                isSaving={isSavingRoster}
            />
            {/* ---------------------------------- */}
        </div>
    );
};

export default AdminModuleManagement;